import {Component, ComponentConfiguration} from "../base";
import { Page } from "../../services/Page";

export type CombolistConfiguration = ComponentConfiguration & {
};
export class Combolist extends Component
{
    static event = {
        CHANGE: "component:combolist:change",
    };

    protected configuration: CombolistConfiguration;
    protected field: HTMLElement;
    protected fieldIcon: HTMLElement;
    protected fieldText: HTMLElement;
    protected initText: string;
    protected itemIconSelector: string;
    protected items: NodeListOf<HTMLElement>;
    protected itemSelectedClass: string;
    protected itemTextSelector: string;
    protected menu: HTMLElement;
    protected menuContent: HTMLElement;
    protected state: { open: boolean, value: any };
    protected unfoldClass: string;
    protected valueAttr: string;

    constructor(...configurations: CombolistConfiguration[])
    {
        const defaults: CombolistConfiguration = {
            content_element: "c-Combolist__menu .c-Menu",
        };

        super(defaults, ...configurations);
    }

    public build(): this
    {
        super.build();

        this.handleCombolistUnfold = this.handleCombolistUnfold.bind(this);
        this.handleCombolistFold = this.handleCombolistFold.bind(this);
        this.handleCombolistToggle = this.handleCombolistToggle.bind(this);
        this.handleItemRelease = this.handleItemRelease.bind(this);
        this.handleGlobalRelease = this.handleGlobalRelease.bind(this);

        if (!this.element)
            return this;

        this.field = this.querySelector(".c-CombolistField");
        this.fieldIcon = this.field.querySelector(".c-CombolistField__icon");
        this.fieldText = this.field.querySelector(".c-CombolistField__text");
        this.initText = this.fieldText ? this.fieldText.innerHTML : "";
        this.menu = this.querySelector(".c-Combolist__menu");
        if(this.menu) {
			this.menuContent = this.menu.querySelector(".c-Menu");
			this.items = this.menu.querySelectorAll(".c-MenuItem");
		}
        this.unfoldClass = "c-Combolist--unfolded";
        this.itemSelectedClass = "c-MenuItem--selected";
        this.itemIconSelector = ".c-MenuItem__icon";
        this.itemTextSelector = ".c-MenuItem__text";

        this.valueAttr = "data-value";

        this.state = {
            open: false,
            value: null,
        };

        this.attachEvents();

        return this;
    }

    public toggle(): this
    {
        if (this.state.open)
            this.fold();
        else
            this.unfold()

        return this;
    }

    public unfold(): this
    {
        this.addClass(this.unfoldClass);
        window.addEventListener("click", this.handleGlobalRelease);

        window.dispatchEvent(new Event("resize") ); //force redraw

        this.state.open = true;

        return this;
    }

    public fold(): this
    {
        this.removeClass(this.unfoldClass);
        window.removeEventListener("click", this.handleGlobalRelease);

        this.state.open = false;

        return this;
    }

    public getItem(value): false | Element
    {
        if (!this.menu || !value)
            return false;

        return this.menu.querySelector(`.s-MenuItem[${this.valueAttr}="${value}"]`);
    }

    public getItems(): NodeListOf<HTMLElement>
    {
        return this.items;
    }

    public getValue(): any
    {
        return this.state.value;
    }

    public addItem(value, text, iconUrl= ""): this
    {
        let iconStyle = iconUrl ? ` style="background-image: url(${iconUrl});"` : ``;

        let html = `<span class="c-MenuItem s-MenuItem--icon-text" data-value="${value}">
                        <span class="c-MenuItem__content">
                            <span class="c-MenuItem__icon">
                                <span class="c-Icon">
                                    <span class="c-Icon__container"${iconStyle}></span>
                                </span>
                            </span>
                            <span class="c-MenuItem__text">${text}</span>
                        </span>
                    </span>`;

        let template = document.createElement("div");
        template.innerHTML = html;
        let item = template.firstChild;

        item.addEventListener("click", this.handleItemRelease);

        this.menuContent.append(item);

        return this;
    }

    public removeItem(item): this
    {
        item.removeEventListener("click", this.handleItemRelease);
        item.remove();

        return this;
    }

    public setValue(value, options = { silent: false, force_set: false }): this
    {
        let defaultOptions = {
            silent: false,
            force_set: false,
        }
        options = Object.assign(defaultOptions, options);

        if (value && value === this.state.value)
        {
            if (!options.force_set)
                return this;
        }

        let newSelectedItem = null;
        if (value)
        {
            newSelectedItem = this.getItem(value);
        }

        let currentSelectedItems = this.menu.querySelectorAll("." + this.itemSelectedClass);

        if (currentSelectedItems)
        {
            currentSelectedItems.forEach
            (
                (selected) =>
                {
                    selected.classList.remove(this.itemSelectedClass);
                },
            );
        }

        if (newSelectedItem)
        {
            let itemIcon = newSelectedItem.querySelector(this.itemIconSelector);
            let itemText = newSelectedItem.querySelector(this.itemTextSelector);
            let itemValue = newSelectedItem.getAttribute(this.valueAttr);

            if (this.fieldIcon)
                this.fieldIcon.innerHTML = itemIcon ? itemIcon.innerHTML : "";

            if (this.fieldText)
                this.fieldText.innerHTML = itemText ? itemText.innerHTML : "";

            this.field.setAttribute(this.valueAttr, itemValue ? itemValue : "");

            newSelectedItem.classList.add(this.itemSelectedClass);
            this.state.value = itemValue;
        }
        else
        {
            if (this.fieldText)
                this.fieldText.innerHTML = this.initText;

            this.field.setAttribute(this.valueAttr, "");
            this.state.value = null;
        }

        if (!options.silent)
        {
            this.trigger(Combolist.event.CHANGE, {
                field: this.field,
                item: newSelectedItem,
                value: this.state.value,
            });
        }

        return this;
    }

    protected attachEvents(): this
    {
        if (this.field)
        {
            this.field.addEventListener("click", this.handleCombolistToggle);
        }

        if (this.items)
        {
            this.items.forEach
            (
                (item) =>
                {
                    item.addEventListener("click", this.handleItemRelease);
                },
            );
        }

        return this;
    }

    protected preparePopperOptions(): object
    {
        let options: object = {};
        let modifiersOptions: object = {};
        let modifiersList: any = [];

        const defaultsOptions: object = {
            // strategy: "fixed",
            placement: "bottom-end",
            modifiers: [
                {
                    name: "preventOverflow",
                    options: {
                        boundary: null,
                    },
                },
            ],
        };

        modifiersList.push({
            name: "offset",
            options: {
                offset: [14, 14],
            },
        });


        if (modifiersList.length)
        {
            modifiersOptions = {
                modifiers: modifiersList,
            };
        }

        options = this.mergeDeep(options, defaultsOptions, modifiersOptions);

        return options;
    }


    protected handleCombolistToggle(event): void
    {
        event.preventDefault();
        // event.stopPropagation();
        this.toggle();
    }

    protected handleCombolistUnfold(event): void
    {
        event.preventDefault();
        // event.stopPropagation();
        this.unfold();
    }

    protected handleCombolistFold(event): void
    {
        event.preventDefault();
        // event.stopPropagation();
        this.fold();
    }

    protected handleGlobalRelease(event): void
    {
        let element = event.target;

        if (!this.contains(element))
            this.fold();
    }

    protected handleItemRelease(event): void
    {
        // event.preventDefault();
        let value = event.target.getAttribute(this.valueAttr);

        if (value)
            this.setValue(value);

        this.fold();
    }
}
